// Resources Library — Wave 6 Day 1 (Priority 4).
//
// Shared component across admin/seller/supply shells. Role prop drives endpoint
// + UI variations. Admin sees ALL resources; seller/supply see filtered subset
// per visibility whitelist (V1 read-only; V1.5+ admin CRUD).

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BookOpen,
  Download,
  ExternalLink,
  FileText,
  Link as LinkIcon,
  ScrollText,
  ShieldCheck,
  Tag,
} from 'lucide-react'
import { API_BASE, USE_REAL_API, apiUrl } from '../../lib/api'
import { formatDate } from '../../lib/datetime'
import { Card } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import type { Resource, ResourceCategory, ResourceType, ShellRole } from '../../mocks/data/resources'
import { useT } from '../../i18n'
import { sellerOnboardingTourI18n } from '../../i18n/onboarding/seller-tour'
import { resourcesLibraryI18n } from '../../i18n/shared/resources-library'
import { useSellerTour } from '../onboarding/seller-tour-context'

const TYPE_ICON: Record<ResourceType, typeof BookOpen> = {
  guide: BookOpen,
  policy: ScrollText,
  template: FileText,
  link: LinkIcon,
}

const TYPE_COLOR: Record<ResourceType, string> = {
  guide: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  policy: 'bg-rose-100 text-rose-800 ring-rose-200',
  template: 'bg-amber-100 text-amber-800 ring-amber-200',
  link: 'bg-[var(--color-surface-muted)] text-[var(--color-text)] ring-[var(--color-border)]',
}

const SHELL_ENDPOINT: Record<ShellRole, string> = {
  admin: '/admin/resources',
  seller: '/seller/resources',
  supply: '/supply/resources',
  b2b: '/admin/resources', // V1.5+ b2b shell
}

export function ResourcesPage({ role }: { role: ShellRole }) {
  const tour = useSellerTour()
  const tourCopy = useT(sellerOnboardingTourI18n)
  // Real-engine mode: the maket Resource shape (type / category / visibility /
  // inline body_markdown / tags) is far richer than the real `resources`
  // document-repository (the BE table stores title + description + a
  // storage_url pointer; the seed collapses FE type→category_key and drops the
  // markdown body + tags). Render the real ResourceRead rows honestly instead
  // of forcing the maket taxonomy onto data that lacks it.
  const library = USE_REAL_API ? (
    <RealResourcesLibrary role={role} />
  ) : (
    <MaketResourcesLibrary role={role} />
  )

  return (
    <div className="space-y-4">
      {role === 'seller' ? (
        <Card className="flex flex-col gap-4 border-[var(--color-border)] bg-[var(--color-porcelain)] p-5 shadow-[var(--shadow-card)] sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-2xl">
            <h2 className="text-base font-semibold text-[var(--color-ink)]">
              {tourCopy.replay.title}
            </h2>
            <p className="mt-1 text-sm leading-6 text-[var(--color-text-muted)]">
              {tourCopy.replay.description}
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            data-tour="seller-tour-replay"
            disabled={!tour}
            title={!tour ? tourCopy.replay.unavailable : undefined}
            onClick={() => tour?.startManual()}
          >
            {tourCopy.replay.action}
          </Button>
        </Card>
      ) : null}
      {library}
    </div>
  )
}

function MaketResourcesLibrary({ role }: { role: ShellRole }) {
  const t = useT(resourcesLibraryI18n)
  const [open, setOpen] = useState<Resource | null>(null)
  const [typeFilter, setTypeFilter] = useState<ResourceType | 'all'>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['resources', role],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}${SHELL_ENDPOINT[role]}`)
      if (!res.ok) throw new Error(`fetch resources ${res.status}`)
      return (await res.json()) as { resources: Resource[] }
    },
  })

  const allResources = data?.resources ?? []
  const filtered =
    typeFilter === 'all'
      ? allResources
      : allResources.filter((r) => r.type === typeFilter)

  // Group by category for visual structure
  const byCategory = filtered.reduce(
    (acc, r) => {
      const cat = r.category
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(r)
      return acc
    },
    {} as Record<ResourceCategory, Resource[]>,
  )

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-text)] flex items-center gap-2">
          <BookOpen size={22} className="text-indigo-600" aria-hidden="true" />
          {t.title}
        </h1>
        <p className="text-xs text-[var(--color-text-muted)] mt-1 flex items-center gap-1.5">
          <ShieldCheck size={12} className="text-emerald-600" aria-hidden="true" />
          {t.subtitle}
          {role === 'admin' ? t.visibleToAll : t.roleFiltered}
        </p>
      </div>

      {/* Type filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-[var(--color-text-muted)] font-medium">{t.filter}</span>
        <button
          type="button"
          onClick={() => setTypeFilter('all')}
          className={`text-xs px-2.5 py-1 rounded-full border transition ${
            typeFilter === 'all'
              ? 'border-indigo-600 appearance-dark:border-[var(--color-info)] bg-indigo-50 appearance-dark:bg-[var(--color-surface-muted)] text-indigo-700 appearance-dark:text-[var(--color-info)] font-medium'
              : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:border-[var(--color-border-strong)]'
          }`}
        >
          {t.all(allResources.length)}
        </button>
        {(['guide', 'policy', 'template', 'link'] as ResourceType[]).map((ty) => {
          const count = allResources.filter((r) => r.type === ty).length
          if (count === 0) return null
          return (
            <button
              key={ty}
              type="button"
              onClick={() => setTypeFilter(ty)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${
                typeFilter === ty
                  ? 'border-indigo-600 appearance-dark:border-[var(--color-info)] bg-indigo-50 appearance-dark:bg-[var(--color-surface-muted)] text-indigo-700 appearance-dark:text-[var(--color-info)] font-medium'
                  : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:border-[var(--color-border-strong)]'
              }`}
            >
              {t.typeCount(t.types[ty], count)}
            </button>
          )
        })}
      </div>

      {/* Content */}
      {isLoading ? (
        <Card className="p-8 text-center text-[var(--color-text-muted)] text-sm">
          {t.loading}
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-[var(--color-text-muted)] italic text-sm">
          {t.noResourcesForFilter}
        </Card>
      ) : (
        <div className="space-y-5">
          {(Object.keys(byCategory) as ResourceCategory[]).map((cat) => (
            <section key={cat}>
              <h2 className="text-xs uppercase tracking-wide text-[var(--color-text-muted)] font-semibold mb-2">
                {t.categories[cat]}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {byCategory[cat].map((r) => {
                  const Icon = TYPE_ICON[r.type]
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => {
                        if (r.type === 'link' && r.external_url) {
                          window.open(r.external_url, '_blank', 'noopener,noreferrer')
                        } else {
                          setOpen(r)
                        }
                      }}
                      className="text-left p-3 border border-[var(--color-border)] rounded hover:border-indigo-300 hover:shadow-sm bg-[var(--color-surface)] transition flex items-start gap-3"
                    >
                      <div
                        className={`flex-shrink-0 w-9 h-9 rounded-full ring-1 flex items-center justify-center ${TYPE_COLOR[r.type]}`}
                      >
                        <Icon size={16} aria-hidden="true" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]">
                            {t.types[r.type]}
                          </span>
                          {role === 'admin' && r.visibility !== 'all' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 ring-1 ring-violet-200">
                              {t.visibility[r.visibility]}
                            </span>
                          )}
                          {r.type === 'link' && (
                            <ExternalLink
                              size={10}
                              className="text-[var(--color-text-muted)]"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                        <h3 className="text-sm font-semibold text-[var(--color-text)] leading-tight">
                          {r.title}
                        </h3>
                        <p className="text-xs text-[var(--color-text-muted)] mt-1 line-clamp-2">
                          {r.description}
                        </p>
                        {r.tags.length > 0 && (
                          <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                            <Tag
                              size={9}
                              className="text-[var(--color-text-muted)]"
                              aria-hidden="true"
                            />
                            {r.tags.slice(0, 4).map((tag) => (
                              <span
                                key={tag}
                                className="text-[10px] text-[var(--color-text-muted)] font-mono"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!open} onOpenChange={(next) => !next && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          {open && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide ring-1 ${TYPE_COLOR[open.type]}`}
                      >
                        {t.types[open.type]}
                      </span>
                      <span className="text-[10px] text-[var(--color-text-muted)]">
                        {t.categories[open.category]}
                      </span>
                    </div>
                    <DialogTitle className="text-lg">{open.title}</DialogTitle>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                      {open.description}
                    </p>
                  </div>
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto p-1">
                {open.body_markdown ? (
                  <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed text-[var(--color-text)] bg-[var(--color-surface-muted)] p-4 rounded border border-[var(--color-border)]">
                    {open.body_markdown}
                  </pre>
                ) : open.external_url ? (
                  <div className="p-4 bg-sky-50 border border-sky-200 rounded text-sm space-y-2">
                    <p>
                      <strong>{open.title}</strong>{t.externalSuffix}
                    </p>
                    <a
                      href={open.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-indigo-600 hover:underline"
                    >
                      <ExternalLink size={12} aria-hidden="true" />
                      {open.external_url}
                    </a>
                  </div>
                ) : (
                  <p className="text-xs text-[var(--color-text-muted)] italic p-4">
                    {t.noContent}
                  </p>
                )}
              </div>
              <div className="border-t border-[var(--color-border)] pt-3 flex items-center justify-between text-[11px] text-[var(--color-text-muted)]">
                <span>
                  {t.published}{formatDate(open.published_at)}
                </span>
                <span>
                  {t.updated}
                  {formatDate(open.updated_at)}
                </span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Real-engine variant ─────────────────────────────────────────────────────

// Outbound shape of GET /api/v1/resources (ResourceRead). The document
// repository stores a gateway-agnostic storage_url pointer + provider; there is
// no inline markdown body or tag list on the real row.
type RealResource = {
  id: string
  title: string
  description: string | null
  storage_provider: string
  storage_url: string
  mime_type: string | null
  shell_visibility: string
  is_archived: boolean
  created_at: string
  updated_at: string
}

// The BE `?shell` narrowing accepts admin/seller/supply; the V2+ b2b shell has
// no server-side surface yet, so it reads the unfiltered (admin Ring 3) list.
const SHELL_QUERY: Record<ShellRole, string> = {
  admin: 'admin',
  seller: 'seller',
  supply: 'supply',
  b2b: 'admin',
}

// 'cdn_url' provider = an external link/template; 'pexify_local' / 's3' = a
// stored document the actor downloads.
function isExternal(r: RealResource): boolean {
  return r.storage_provider === 'cdn_url'
}

function RealResourcesLibrary({ role }: { role: ShellRole }) {
  const t = useT(resourcesLibraryI18n)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['resources', 'real', role],
    queryFn: async () => {
      // apiUrl() already prefixes the /api/v1 base; the BE resources router is
      // mounted at /api/v1/resources (not a per-shell path), narrowed via ?shell.
      const res = await fetch(apiUrl(`/resources?shell=${SHELL_QUERY[role]}`))
      if (!res.ok) throw new Error(`fetch resources ${res.status}`)
      return (await res.json()) as { items: RealResource[]; total: number }
    },
  })

  const resources = (data?.items ?? []).filter((r) => !r.is_archived)

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-text)] flex items-center gap-2">
          <BookOpen size={22} className="text-indigo-600" aria-hidden="true" />
          {t.title}
        </h1>
        <p className="text-xs text-[var(--color-text-muted)] mt-1 flex items-center gap-1.5">
          <ShieldCheck size={12} className="text-emerald-600" aria-hidden="true" />
          {t.subtitle}
          {role === 'admin' ? t.visibleToAll : t.roleFiltered}
        </p>
      </div>

      {isLoading ? (
        <Card className="p-8 text-center text-[var(--color-text-muted)] text-sm">{t.loading}</Card>
      ) : isError ? (
        <Card className="p-8 text-center text-rose-600 text-sm">
          {t.loadError}
        </Card>
      ) : resources.length === 0 ? (
        <Card className="p-8 text-center text-[var(--color-text-muted)] italic text-sm">
          {t.noResources}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {resources.map((r) => {
            const external = isExternal(r)
            const Icon = external ? LinkIcon : FileText
            return (
              <a
                key={r.id}
                href={r.storage_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-left p-3 border border-[var(--color-border)] rounded hover:border-indigo-300 hover:shadow-sm bg-[var(--color-surface)] transition flex items-start gap-3"
              >
                <div className="flex-shrink-0 w-9 h-9 rounded-full ring-1 ring-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-text)] flex items-center justify-center">
                  <Icon size={16} aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]">
                      {external ? t.externalLink : t.document}
                    </span>
                    {r.mime_type && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)] font-mono">
                        {r.mime_type}
                      </span>
                    )}
                    {external ? (
                      <ExternalLink size={10} className="text-[var(--color-text-muted)]" aria-hidden="true" />
                    ) : (
                      <Download size={10} className="text-[var(--color-text-muted)]" aria-hidden="true" />
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-[var(--color-text)] leading-tight">
                    {r.title}
                  </h3>
                  {r.description && (
                    <p className="text-xs text-[var(--color-text-muted)] mt-1 line-clamp-2">
                      {r.description}
                    </p>
                  )}
                  <div className="mt-1.5 flex items-center gap-1 text-[10px] text-[var(--color-text-muted)]">
                    <Tag size={9} aria-hidden="true" />
                    {t.updated}{formatDate(r.updated_at)}
                  </div>
                </div>
              </a>
            )
          })}
        </div>
      )}
    </div>
  )
}
